import React, { useState, useMemo } from "react";
import {
  createEliminationMatches,
  deleteEliminationMatches,
} from "services/matchService";
import * as Sentry from "@sentry/react";
import DualDivisionEliminationManager from "./DualDivisionEliminationManager";
import CrosspoolEliminationManager from "./CrosspoolEliminationManager";

const EliminationPhaseManager = ({ tournament, location, matches, reloadMatches }) => {
  const [bracketSize, setBracketSize] = useState(4);
  const [loading, setLoading] = useState(false);

  const divisionNames = useMemo(
    () => Object.keys(tournament?.teams || {}),
    [tournament]
  );

  const totalMergedTeams = useMemo(() => {
    return tournament ? Object.values(tournament.teams || {}).flat().length : 0;
  }, [tournament]);

  const eliminationMatches = useMemo(
    () => matches?.filter((m) => m.phase === "elimination") || [],
    [matches]
  );

  const hasCrosspoolMatches = useMemo(
    () => matches?.some((m) => m.phase === "crosspool") || false,
    [matches]
  );

  const hasEliminationStarted = useMemo(() => {
    return eliminationMatches.length > 0;
  }, [eliminationMatches]);

  const getAvailableBracketSizes = (teamsCount) => {
    const options = [];
    if (teamsCount >= 2) options.push(2);
    if (teamsCount >= 3) options.push(4);
    if (teamsCount >= 5) options.push(8);
    if (teamsCount >= 9) options.push(16);
    if (teamsCount >= 17) options.push(32);
    if (teamsCount >= 33) options.push(64);
    return options;
  };

  const handleStartElimination = async () => {
    if (!tournament?.id) return;
    setLoading(true);
    try {
      const divisionName =
        divisionNames.length === 1 ? divisionNames[0] : "merged";
      await createEliminationMatches({
        tournamentId: tournament.id,
        divisionName,
        bracketSize,
      });
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
      console.error("❌ Failed to create elimination matches:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelElimination = async () => {
    if (!tournament?.id) return;
    const confirm = window.confirm(
      "Êtes-vous sûr de vouloir annuler cette phase éliminatoire ?"
    );
    if (!confirm) return;

    setLoading(true);
    try {
      const divisionName =
        divisionNames.length === 1 ? divisionNames[0] : "merged";
      await deleteEliminationMatches({
        tournamentId: tournament.id,
        divisionName: divisionName,
      });
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
      console.error("❌ Failed to delete elimination matches:", error);
    } finally {
      setLoading(false);
    }
  };

  // ⬇️ If crosspool matches exist, use crosspool elimination
  if (hasCrosspoolMatches && divisionNames.length === 2) {
    return (
      <CrosspoolEliminationManager
        tournament={tournament}
        location={location}
        matches={matches}
        reloadMatches={reloadMatches}
      />
    );
  }

  // ⬇️ If split divisions mode, defer rendering to child component
  if (tournament?.makeDivisions) {
    return (
      <DualDivisionEliminationManager
        tournament={tournament}
        matches={matches}
        reloadMatches={reloadMatches}
      />
    );
  }

  return (
    <div className="w-full md:w-1/2 flex flex-col items-center bg-slate-800 p-6 space-y-6">
      <h2 className="text-2xl font-bold text-slate-50">Phase Éliminatoire</h2>

      {hasEliminationStarted ? (
        <button
          onClick={handleCancelElimination}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
        >
          {loading ? "Annulation..." : "Annuler l'éliminatoire"}
        </button>
      ) : (
        <>
          <div className="flex flex-col items-center space-y-2 mt-4">
            <label htmlFor="bracketSize" className="text-slate-50">
              Taille du bracket :
            </label>
            <select
              id="bracketSize"
              value={bracketSize}
              onChange={(e) => setBracketSize(Number(e.target.value))}
              className="rounded-md px-4 py-2 text-black text-sm font-bold"
            >
              {getAvailableBracketSizes(totalMergedTeams).map((size) => (
                <option key={size} value={size}>
                  {size} équipes
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleStartElimination}
            disabled={loading}
            className="mt-6 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Démarrage en cours..."
              : "Démarrer la Phase Éliminatoire"}
          </button>
        </>
      )}
    </div>
  );
};

export default EliminationPhaseManager;
